import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listNotifications, markNotificationsRead } from '../../api/account.ts';
import { formatRelative } from '../../lib/format.ts';
import { IconCheck } from '../../lib/icons.tsx';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => listNotifications(false),
    // WebSocket 알림은 파일 변경용이라, 계정 알림은 가볍게 폴링한다
    refetchInterval: 60_000,
  });

  const markAll = useMutation({
    mutationFn: () => markNotificationsRead({ all: true }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['notifications'] }); },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('mousedown', onClick); };
  }, [open]);

  const unread = data?.unread_count ?? 0;
  const items = (data?.items ?? []).slice(0, 12);

  return (
    <div style={{ position: 'relative' }} ref={boxRef}>
      <button
        className="btn btn--ghost btn--icon"
        aria-label={unread > 0 ? `알림 ${unread}건` : '알림'}
        style={{ position: 'relative' }}
        onClick={() => { setOpen(!open); }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="notif__dot">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif">
          <div className="notif__head">
            <span className="spacer">알림</span>
            {unread > 0 && (
              <button className="btn btn--sm btn--ghost" onClick={() => { markAll.mutate(); }}>
                <IconCheck size={13} /> 모두 읽음
              </button>
            )}
          </div>

          {items.length === 0 && (
            <div style={{ padding: 'var(--sp-5)', textAlign: 'center' }} className="muted">
              새 알림이 없습니다
            </div>
          )}

          {items.map((item) => {
            const inner = (
              <>
                <div className="notif__title">{item.title}</div>
                {item.body && <div className="notif__body">{item.body}</div>}
                <div className="notif__time">{formatRelative(item.created_at)}</div>
              </>
            );
            return item.link ? (
              <Link
                key={item.id} to={item.link} className="notif__item"
                data-unread={item.read_at === null}
                onClick={() => { setOpen(false); }}
              >
                {inner}
              </Link>
            ) : (
              <div key={item.id} className="notif__item" data-unread={item.read_at === null}>
                {inner}
              </div>
            );
          })}

          <Link
            to="/account?tab=notifications"
            className="notif__item"
            style={{ textAlign: 'center', fontSize: 13 }}
            onClick={() => { setOpen(false); }}
          >
            전체 보기
          </Link>
        </div>
      )}
    </div>
  );
}
